/**
 * Post-purchase confirmation — scenario resolution + copy (PANDA post-upgrade
 * confirmation experience).
 *
 * After any successful Stripe checkout return or in-app purchase completion, the
 * app shows a prominent confirmation modal instead of relying on a bottom-right
 * toast. This module is the single source of truth for WHAT that modal says: it
 * turns the raw purchase signals a call site already has (which plan, how many
 * credits, the price) into scenario-specific copy + a benefit recap.
 *
 * It is deliberately pure (no React, no DOM) so the copy for every scenario —
 * new subscription, plan upgrade, monthly-credit increase, and one-off credit
 * top-up — can be reasoned about and unit-tested in isolation. The modal
 * component and the store are thin shells around `resolvePurchaseConfirmation`.
 */
import {
  PLANS,
  PLAN_RANK,
  planRank,
  type Cadence,
  type PlanId,
} from "@/lib/plans";
import { PRICING_TIERS, type PricingTierId } from "@/lib/pricing/credit-tiers";

/**
 * What the call site tells us happened. A discriminated union on `kind` so a
 * plan change and a credit top-up can't be confused. Every field beyond the
 * discriminant is optional-friendly: call sites pass whatever the freshly
 * reconciled snapshot gave them, and the resolver degrades to correct — if
 * less specific — copy when a field is missing.
 */
export type PurchaseConfirmationInput =
  | {
      kind: "plan";
      /** The plan the user is on AFTER the purchase (e.g. "pro"). */
      planId: PlanId;
      /**
       * The plan the user was on BEFORE, when the call site knows it. Lets us
       * distinguish a brand-new subscription (was free) from an upgrade (was a
       * lower paid tier) from a same-plan monthly-credit increase.
       */
      previousPlanId?: PlanId | null;
      /** New monthly credit allocation for the plan, if known. */
      monthlyCredits?: number | null;
      /** New recurring price in whole USD, if known. */
      monthlyPriceUsd?: number | null;
      cadence?: Cadence;
      /** Where the confirmation was triggered from (analytics only). */
      source: string;
      projectId?: string | null;
      /** Optional CTA override. When omitted a sensible default is chosen. */
      cta?: { label?: string; href?: string | null } | null;
    }
  | {
      kind: "credits";
      /** Credits added by the top-up, if the call site knows the amount. */
      creditsAdded?: number | null;
      /** Total spendable balance after the top-up landed, if known. */
      balanceAfter?: number | null;
      source: string;
      projectId?: string | null;
      cta?: { label?: string; href?: string | null } | null;
    }
  | {
      // $1 trial just STARTED (PANDA-1500). Distinct from "plan" — the user is
      // trialing, not a paying subscriber, so the copy must say "Pro trial" +
      // the 100 trial credits, never "Welcome to Pro / $29 renews monthly".
      kind: "trial";
      /** Trial credits granted now (100). */
      credits: number;
      /** Trial length in days (3). */
      days: number;
      /** Recurring price after the trial, whole USD (29), if known. */
      renewsPriceUsd?: number | null;
      /** Recurring monthly credits after conversion (300), if known. */
      renewsCredits?: number | null;
      source: string;
      projectId?: string | null;
      cta?: { label?: string; href?: string | null } | null;
    };

/**
 * Stable scenario identifier. Drives analytics and is a precise handle for QA
 * ("test scenario=plan_upgrade"). Copy may evolve; these keys should not.
 */
export type PurchaseConfirmationScenario =
  | "new_subscription"
  | "plan_upgrade"
  | "monthly_credits_increase"
  | "plan_active"
  | "purchase_credits"
  | "trial_active";

/** The fully-resolved, render-ready view model for the modal. */
export interface PurchaseConfirmationView {
  scenario: PurchaseConfirmationScenario;
  kind: PurchaseConfirmationInput["kind"];
  /** Plan involved, when the scenario is a plan change (else null). */
  planId: PlanId | null;
  /** Modal heading, e.g. "Welcome to Grow". */
  title: string;
  /** One-line supporting sentence under the title. */
  subtitle: string;
  /** The big number line, e.g. "1,000 credits / month" or "+2,500 credits". */
  headline: string;
  /** Secondary money/timing note, e.g. "$97/mo · renews monthly". */
  priceNote: string | null;
  /** Short label for the benefit list, e.g. "What's unlocked". */
  benefitsLabel: string;
  /** The most relevant unlocked benefits. */
  benefits: string[];
  /** Primary CTA label. */
  ctaLabel: string;
  /** Primary CTA href when the CTA should navigate; null = just dismiss. */
  ctaHref: string | null;
  /** Whether the celebratory (confetti) treatment is warranted. */
  celebrate: boolean;
  /** Passed through for analytics. */
  source: string;
  projectId: string | null;
}

/** en-US grouped integer, e.g. 2500 → "2,500". */
function fmt(n: number): string {
  return Math.round(n).toLocaleString("en-US");
}

/** Map a plan id (incl. legacy "starter") to its pricing-tier content key. */
function tierKeyFor(plan: PlanId): PricingTierId {
  if (plan === "free") return "free";
  // Legacy "starter" rows render the "pro" tier's content (see lib/plans).
  if (plan === "starter") return "pro";
  return plan;
}

/**
 * The benefit recap for a plan. Uses the marketing feature list from the
 * pricing catalog (the same bullets the pricing page shows), so the recap can
 * never drift from what the plan actually promises.
 */
function planBenefits(plan: PlanId): string[] {
  return PRICING_TIERS[tierKeyFor(plan)].features.slice(0, 5);
}

function planName(plan: PlanId): string {
  return PLANS[plan]?.name ?? "your plan";
}

/**
 * Decide which plan scenario applies from the before/after plans and credit
 * allocation. `previousPlanId` is best-effort — when it's missing we fall back
 * to the neutral "plan_active" copy, which reads correctly for both a new
 * subscription and an upgrade.
 */
function resolvePlanScenario(
  input: Extract<PurchaseConfirmationInput, { kind: "plan" }>,
): PurchaseConfirmationScenario {
  const { planId, previousPlanId } = input;
  if (previousPlanId == null) return "plan_active";
  if (previousPlanId === "free") return "new_subscription";
  // Same tier, more monthly credits bought via the credit dropdown.
  if (previousPlanId === planId) return "monthly_credits_increase";
  // Known paid → higher paid tier.
  if (PLAN_RANK[planId] > planRank(previousPlanId)) return "plan_upgrade";
  // Anything else (equal/lower rank with a known previous) — treat as active.
  return "plan_active";
}

function resolvePlanView(
  input: Extract<PurchaseConfirmationInput, { kind: "plan" }>,
): PurchaseConfirmationView {
  const scenario = resolvePlanScenario(input);
  const name = planName(input.planId);
  const credits = input.monthlyCredits ?? PLANS[input.planId]?.buildCreditsPerMonth ?? null;
  const price = input.monthlyPriceUsd ?? null;

  const headline = credits != null ? `${fmt(credits)} credits / month` : `${name} plan`;

  // "renews monthly/annually" reads as a reassurance, not a warning: the user
  // just paid and we're telling them what recurs.
  const priceNote =
    price != null && price > 0
      ? `$${fmt(price)}/${input.cadence === "annual" ? "yr" : "mo"} · renews ${
          input.cadence === "annual" ? "annually" : "monthly"
        }`
      : null;

  let title: string;
  let subtitle: string;
  switch (scenario) {
    case "new_subscription":
      title = `Welcome to ${name}`;
      subtitle = "Your subscription is active — here's what you just unlocked.";
      break;
    case "plan_upgrade":
      title = `You're on ${name} now`;
      subtitle = "Upgrade complete. Your new plan is live.";
      break;
    case "monthly_credits_increase":
      title = "More monthly credits unlocked";
      subtitle = credits != null
        ? `You're now on ${fmt(credits)} credits every month.`
        : "Your new monthly credit allowance is active.";
      break;
    default:
      title = `You're on ${name}`;
      subtitle = "Your plan is active — here's what's included.";
      break;
  }

  return {
    scenario,
    kind: "plan",
    planId: input.planId,
    title,
    subtitle,
    headline,
    priceNote,
    benefitsLabel:
      scenario === "plan_upgrade" ? "What's unlocked" : "What's included",
    benefits: planBenefits(input.planId),
    ctaLabel: input.cta?.label ?? "Start building",
    ctaHref: input.cta?.href ?? null,
    celebrate: true,
    source: input.source,
    projectId: input.projectId ?? null,
  };
}

function resolveCreditsView(
  input: Extract<PurchaseConfirmationInput, { kind: "credits" }>,
): PurchaseConfirmationView {
  const added = input.creditsAdded ?? null;
  const balance = input.balanceAfter ?? null;

  // Hero number: the amount added when we know it, otherwise the new balance,
  // otherwise a plain acknowledgement. Never repeat the title as the headline.
  const headline =
    added != null
      ? `+${fmt(added)} credits`
      : balance != null
        ? `${fmt(balance)} credits`
        : "Credits added";

  // Only claim things that are unambiguously true. We deliberately do NOT say
  // "never expire" — top-up credits sit in the same pool as plan credits, which
  // roll over only ~1 cycle and are capped at renewal (lib/credits/grant.ts).
  const benefits: string[] = [];
  // Show the balance as a benefit only when it isn't already the headline.
  if (added != null && balance != null) {
    benefits.push(`${fmt(balance)} credits available now`);
  }
  benefits.push("Ready to spend on your next build");
  benefits.push("One credit pool — powers building and Grow");

  return {
    scenario: "purchase_credits",
    kind: "credits",
    planId: null,
    title: "Top-up complete",
    subtitle:
      added != null
        ? `Your top-up of ${fmt(added)} credits is ready to use.`
        : "Your top-up is ready to use.",
    headline,
    priceNote: null,
    benefitsLabel: "What this means",
    benefits,
    ctaLabel: input.cta?.label ?? "Continue building",
    ctaHref: input.cta?.href ?? null,
    // A top-up is a real purchase and deserves acknowledgement, but a lighter
    // one than a plan change — no confetti so frequent top-ups don't feel loud.
    celebrate: false,
    source: input.source,
    projectId: input.projectId ?? null,
  };
}

function resolveTrialView(
  input: Extract<PurchaseConfirmationInput, { kind: "trial" }>,
): PurchaseConfirmationView {
  const { credits, days } = input;
  const renewsPrice = input.renewsPriceUsd ?? null;
  const renewsCredits = input.renewsCredits ?? null;

  return {
    scenario: "trial_active",
    kind: "trial",
    planId: "pro",
    title: "You're on the Pro trial",
    subtitle: `Spend your ${fmt(credits)} credits and ship something real — your trial becomes Pro on day ${days}.`,
    headline: `${fmt(credits)} credits · ${days} days`,
    // Reassurance, not a warning: state the renewal terms the trial discloses.
    priceNote:
      renewsPrice != null && renewsPrice > 0
        ? `Renews to $${fmt(renewsPrice)}/mo${
            renewsCredits != null ? ` · ${fmt(renewsCredits)} credits` : ""
          } on day ${days} — cancel anytime before then`
        : `Cancel anytime before day ${days} — no charge`,
    benefitsLabel: "What's included",
    benefits: [
      `${fmt(credits)} credits, ready to spend now`,
      "Publish to your own custom domain",
      "Every project you build is yours to keep",
    ],
    ctaLabel: input.cta?.label ?? "Start building",
    ctaHref: input.cta?.href ?? null,
    celebrate: true,
    source: input.source,
    projectId: input.projectId ?? null,
  };
}

/**
 * Turn raw purchase signals into the render-ready confirmation view. Pure and
 * total: every input produces a coherent view, even with only the discriminant
 * set.
 */
export function resolvePurchaseConfirmation(
  input: PurchaseConfirmationInput,
): PurchaseConfirmationView {
  switch (input.kind) {
    case "plan":
      return resolvePlanView(input);
    case "trial":
      return resolveTrialView(input);
    default:
      return resolveCreditsView(input);
  }
}
